/**
 * EditSavedReportDialog
 *
 * Opened from the Edit action on Saved Reports. For Infralytiqs SDK shares it
 * regenerates the embed snippet. For Credentials shares it updates suites,
 * default suite, theme, and optionally resets the shareable password.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, IconButton, InputAdornment, InputLabel, ListItemText,
  MenuItem, OutlinedInput, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import { Close as CloseIcon, ContentCopy, Edit as EditIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { RootState } from 'state/store';
import { API_BASE_URL } from 'envConfig';
import { THEME_NAME_PROFESSIONAL } from 'theme';
import { preferenceToShareThemeType } from 'theme/themeMode';
import apiClient from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import {
  COLOR_FIELD_GROUPS,
  ColorField,
  ShareSuiteOption,
  ShareTheme,
  ShareThemeType,
  buildCodeSnippet,
  normalizeShareTheme,
  normalizeShareThemeType,
  parseSnippetConfig,
} from '../DirectReports/shareReportSnippet';

interface SavedShareForEdit {
  _id: string;
  name: string;
  snippet: string;
  server: string;
  reportSuites: string[];
  securityType?: string;
  shareUsername?: string;
  defaultReportSuite?: string;
  theme?: string;
  themeType?: string;
}

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  reportSuites?: ShareSuiteOption[];
}

interface EditSavedReportDialogProps {
  open: boolean;
  onClose: () => void;
  share: SavedShareForEdit | null;
  onSaved: () => void;
}

export const EditSavedReportDialog: React.FC<EditSavedReportDialogProps> = ({
  open,
  onClose,
  share,
  onSaved,
}) => {
  const apiBaseUrl = useSelector(
    (state: RootState) => state.clientConfig.data?.apiBaseUrl || API_BASE_URL,
  );
  const domainTheme = useSelector((state: RootState) => state.clientConfig.data?.theme);
  const globalMode = useSelector((state: RootState) => state.global.mode);

  const defaultThemeParam: ShareTheme = domainTheme === THEME_NAME_PROFESSIONAL ? 'PROFESSIONAL' : 'DEFAULT';
  const defaultThemeType: ShareThemeType = preferenceToShareThemeType(globalMode);

  const [suites, setSuites] = useState<ShareSuiteOption[]>([]);
  const [loadingSuites, setLoadingSuites] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [selectedSuiteIds, setSelectedSuiteIds] = useState<string[]>([]);
  const [defaultShareSuiteId, setDefaultShareSuiteId] = useState('');
  const [themeParam, setThemeParam] = useState<ShareTheme>(defaultThemeParam);
  const [themeType, setThemeType] = useState<ShareThemeType>(defaultThemeType);
  const [colors, setColors] = useState<Record<string, string>>({});
  const [generatedSnippet, setGeneratedSnippet] = useState('');
  const [copied, setCopied] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchSuitesForServer = useCallback(async (serverName: string) => {
    setLoadingSuites(true);
    setLoadError('');
    try {
      const res = await apiClient.get('/il/settings/module-configs');
      const list: ModuleConfig[] = Array.isArray(res?.data)
        ? res.data
        : (res?.data?.moduleConfigurations ?? []);
      const cfg = list.find((c) => c.moduleType === 'ClickHouse' && c.name === serverName);
      if (!cfg) {
        setSuites([]);
        setLoadError(`ClickHouse server "${serverName}" was not found in settings`);
        return;
      }
      setSuites(cfg.reportSuites ?? []);
    } catch (error: any) {
      Logger.error('EditSavedReportDialog', `Failed to load report suites: ${error}`);
      setLoadError(error?.message || 'Failed to load report suites');
      setSuites([]);
    } finally {
      setLoadingSuites(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !share) return;

    void fetchSuitesForServer(share.server);

    const isCredentials = share.securityType === 'Credentials';
    if (isCredentials) {
      setThemeParam(normalizeShareTheme(share.theme ?? defaultThemeParam));
      setThemeType(normalizeShareThemeType(share.themeType ?? defaultThemeType));
      setColors({});
    } else {
      const parsed = parseSnippetConfig(share.snippet);
      setThemeParam(normalizeShareTheme(parsed?.theme ?? defaultThemeParam));
      setThemeType(normalizeShareThemeType(parsed?.themetype ?? defaultThemeType));
      setColors((parsed?.colors as Record<string, string>) ?? {});
    }
    setGeneratedSnippet('');
    setCopied(false);
    setSaveError('');
    setResetPassword('');
    setConfirmPassword('');
    setShowResetPassword(false);
  }, [open, share, fetchSuitesForServer, defaultThemeParam, defaultThemeType]);

  // Once suites are loaded, map saved suite names → ids for the multi-select.
  useEffect(() => {
    if (!open || !share || suites.length === 0) {
      if (!open) {
        setSelectedSuiteIds([]);
        setDefaultShareSuiteId('');
      }
      return;
    }
    const names = share.reportSuites?.length
      ? share.reportSuites
      : (parseSnippetConfig(share.snippet)?.reportsuites ?? []);
    const ids = suites.filter((s) => names.includes(s.name)).map((s) => s._id);
    setSelectedSuiteIds(ids);
    const defaultName = (share.defaultReportSuite || '').trim().toLowerCase();
    const defaultId = defaultName
      ? suites.find((s) => s.name.toLowerCase() === defaultName && ids.includes(s._id))?._id
      : undefined;
    setDefaultShareSuiteId(defaultId || ids[0] || '');
  }, [open, share, suites]);

  const selectedSuiteNames = useMemo(
    () => suites.filter((s) => selectedSuiteIds.includes(s._id)).map((s) => s.name),
    [suites, selectedSuiteIds],
  );

  const selectedSuites = useMemo(
    () => suites.filter((s) => selectedSuiteIds.includes(s._id)),
    [suites, selectedSuiteIds],
  );

  const isCredentials = share?.securityType === 'Credentials';
  const defaultShareSuiteName = selectedSuites.find((s) => s._id === defaultShareSuiteId)?.name
    || selectedSuiteNames[0]
    || '';
  const resettingPassword = resetPassword.length > 0 || confirmPassword.length > 0;
  const passwordResetValid = !resettingPassword
    || (resetPassword.length >= 6 && resetPassword === confirmPassword);

  const applySuiteSelection = (ids: string[]) => {
    setSelectedSuiteIds(ids);
    setDefaultShareSuiteId((prev) => (ids.includes(prev) ? prev : (ids[0] || '')));
    setGeneratedSnippet('');
    setCopied(false);
  };

  const setColor = (key: string, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    setGeneratedSnippet('');
    setCopied(false);
  };

  const buildSnippet = () => {
    if (!share) return '';
    return buildCodeSnippet(
      window.location.origin,
      apiBaseUrl,
      share.server,
      selectedSuiteNames,
      themeParam,
      themeType,
      colors,
    );
  };

  const handleGenerate = () => {
    setGeneratedSnippet(buildSnippet());
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!generatedSnippet) return;
    try {
      await navigator.clipboard.writeText(generatedSnippet);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleSave = async () => {
    if (!share || selectedSuiteIds.length === 0) return;
    if (isCredentials && resettingPassword && !passwordResetValid) return;
    setSaving(true);
    setSaveError('');
    try {
      const payload: Record<string, unknown> = {
        reportSuites: selectedSuiteNames,
        defaultReportSuite: defaultShareSuiteName,
        server: share.server,
        theme: themeParam,
        themeType,
      };
      if (!isCredentials) {
        payload.snippet = generatedSnippet || buildSnippet();
      }
      if (isCredentials && resettingPassword) {
        payload.password = resetPassword;
      }
      await apiClient.patch(`/il/share/report-suites/${share._id}`, payload);
      onSaved();
      onClose();
    } catch (error: any) {
      Logger.error('EditSavedReportDialog', `Failed to update saved share: ${error}`);
      setSaveError(error?.message || 'Error updating saved report');
    } finally {
      setSaving(false);
    }
  };

  const canGenerate = Boolean(share?.server) && selectedSuiteIds.length > 0 && !loadingSuites;
  const canSave = canGenerate && (!isCredentials || passwordResetValid);

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth={isCredentials ? 'sm' : 'md'}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon fontSize="small" />
          <Typography variant="h6" component="span" fontWeight={600}>
            Edit saved report
          </Typography>
        </Box>
        <Tooltip title="Close">
          <span>
            <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Name"
          value={share?.name || ''}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="ClickHouse Server"
          value={share?.server || ''}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
        />

        {loadError && <Alert severity="warning">{loadError}</Alert>}

        <FormControl size="small" fullWidth disabled={loadingSuites || suites.length === 0}>
          <InputLabel>Report Suites</InputLabel>
          <Select
            multiple
            label="Report Suites"
            value={selectedSuiteIds}
            onChange={(e) => {
              const v = e.target.value;
              applySuiteSelection(typeof v === 'string' ? v.split(',') : v);
            }}
            input={<OutlinedInput label="Report Suites" />}
            renderValue={(selected) => suites
              .filter((s) => (selected as string[]).includes(s._id))
              .map((s) => s.name)
              .join(', ')}
          >
            {suites.length === 0 && (
              <MenuItem value="" disabled>No report suites available</MenuItem>
            )}
            {suites.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                <Checkbox checked={selectedSuiteIds.includes(s._id)} />
                <ListItemText primary={s.name} secondary={s.description} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedSuiteIds.length > 0 && (
          <FormControl size="small" fullWidth disabled={loadingSuites}>
            <InputLabel>Default Report Suite</InputLabel>
            <Select
              label="Default Report Suite"
              value={selectedSuiteIds.includes(defaultShareSuiteId) ? defaultShareSuiteId : (selectedSuiteIds[0] || '')}
              onChange={(e) => { setDefaultShareSuiteId(e.target.value as string); setGeneratedSnippet(''); setCopied(false); }}
            >
              {selectedSuites.map((s) => (
                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mx: 1.75 }}>
              Loaded first on the shareable link. Recipients can still switch to the other selected suites.
            </Typography>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
            <InputLabel>Theme</InputLabel>
            <Select
              label="Theme"
              value={themeParam}
              onChange={(e) => { setThemeParam(e.target.value as ShareTheme); setGeneratedSnippet(''); setCopied(false); }}
            >
              <MenuItem value="DEFAULT">Default</MenuItem>
              <MenuItem value="PROFESSIONAL">Professional</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
            <InputLabel>Theme Type</InputLabel>
            <Select
              label="Theme Type"
              value={themeType}
              onChange={(e) => { setThemeType(e.target.value as ShareThemeType); setGeneratedSnippet(''); setCopied(false); }}
            >
              <MenuItem value="LIGHT">Light</MenuItem>
              <MenuItem value="DARK">Dark</MenuItem>
              <MenuItem value="AUTOMATIC">Automatic</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isCredentials && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Shareable credentials
              </Typography>
            </Divider>
            <TextField
              size="small"
              fullWidth
              label="Username"
              value={share?.shareUsername || ''}
              InputProps={{ readOnly: true }}
            />
            <TextField
              size="small"
              fullWidth
              type={showResetPassword ? 'text' : 'password'}
              label="New password"
              helperText="Leave blank to keep the current password. New password must be at least 6 characters."
              value={resetPassword}
              onChange={(e) => { setResetPassword(e.target.value); setSaveError(''); }}
              autoComplete="new-password"
              disabled={saving}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowResetPassword((v) => !v)}
                      edge="end"
                    >
                      {showResetPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              fullWidth
              type={showResetPassword ? 'text' : 'password'}
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setSaveError(''); }}
              autoComplete="new-password"
              disabled={saving}
              error={resettingPassword && resetPassword !== confirmPassword}
              helperText={resettingPassword && resetPassword !== confirmPassword ? 'Passwords do not match' : ' '}
            />
          </Box>
        )}

        {!isCredentials && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Color overrides (optional)
            </Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary">
            Leave a field blank to keep the selected theme&apos;s default. Values can be any HEX color
            (e.g. <code>#1473E6</code>); they are forced onto the embedded report suite.
          </Typography>
          {COLOR_FIELD_GROUPS.map((grp) => (
            <Box key={grp.group} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {grp.group}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {grp.fields.map((f) => (
                  <ColorField
                    key={f.key}
                    label={f.label}
                    value={colors[f.key] || ''}
                    onChange={(v) => setColor(f.key, v)}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        )}

        {!isCredentials && (
          <>
            <Button variant="contained" color="secondary" onClick={handleGenerate} disabled={!canGenerate}>
              Generate code snippet
            </Button>

            {generatedSnippet && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label="Code snippet"
                  value={generatedSnippet}
                  size="small"
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={16}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  onFocus={(e) => e.target.select()}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<ContentCopy />} onClick={() => void handleCopy()} variant="outlined" color="secondary">
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}

        {saveError && <Alert severity="error">{saveError}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => void handleSave()}
          disabled={!canSave || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSavedReportDialog;
